import { useId, useRef, useState } from 'react';

import { cx } from '../../lib/cx';
import type { TabsProps } from './types';

/**
 * Tabs following the WAI-ARIA pattern: one tab stop, arrow keys move between tabs, Home and
 * End jump, the panel is labelled by its tab. With `keepMounted` the inactive panels stay in the
 * tree (hidden), so their form state survives a switch.
 */
export const Tabs = ({ items, value, onChange, label, keepMounted = false }: TabsProps) => {
  const [internal, setInternal] = useState(items[0]?.id ?? '');
  const selected = value ?? internal;
  const baseId = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (id: string, focusIndex?: number) => {
    setInternal(id);
    onChange?.(id);
    if (focusIndex !== undefined) {
      refs.current[focusIndex]?.focus();
    }
  };

  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    const last = items.length - 1;
    const map: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const next = map[event.key];
    if (next !== undefined) {
      event.preventDefault();
      select(items[next]?.id ?? selected, next);
    }
  };

  return (
    <div>
      <div
        role="tablist"
        aria-label={label}
        className="flex gap-2 overflow-x-auto border-b border-gray-200"
      >
        {items.map((item, index) => {
          const active = item.id === selected;
          return (
            <button
              key={item.id}
              ref={(element) => {
                refs.current[index] = element;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${item.id}`}
              aria-selected={active}
              aria-controls={`${baseId}-panel-${item.id}`}
              tabIndex={active ? 0 : -1}
              onClick={() => select(item.id)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cx(
                'control-touch -mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                active
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700',
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          role="tabpanel"
          id={`${baseId}-panel-${item.id}`}
          aria-labelledby={`${baseId}-tab-${item.id}`}
          hidden={item.id !== selected}
          className="pt-4"
        >
          {keepMounted || item.id === selected ? item.content : null}
        </div>
      ))}
    </div>
  );
};
